// batch-67 middelen-inname (FO-505 §A3) — het waardevoorstel voor een middel dat
// bij de inname ontbreekt of beschadigd is.
//
// Pure functie: geen DB, geen klok, geen I/O. De API haalt de prijzen en datums
// op en voert ze hier in; het formulier toont het voorstel met de toelichting.
//
//   basis        aanschafwaarde van het exemplaar, anders de catalogusprijs,
//                anders de laatste inkoopregel (in die volgorde)
//   afschrijving lineair per categorie volgens de instelbare tabel, gerekend
//                vanaf de aanschafdatum of (als die onbekend is) de uitgifte
//   ondergrens   het minimum restwaardepercentage van de basis
//
// Geen prijs of geen datum bekend: het voorstel blijft LEEG. Er wordt nooit een
// bedrag verzonnen; de gebruiker vult het dan zelf in, met reden.
//
// Los van `employee-cost-autofill.ts`: dat is kostprijsberekening (telefoon 60,
// gereedschap 24 maanden); dit is de waarde die op een medewerker verhaald wordt.
//
// Geld rekent in hele centen (integers); pas het eindbedrag wordt afgerond.

import type { InnameAfschrijvingstabel } from '@streep/types'
import { heleMaandenTussen } from './ketenregeling'

export const INNAME_AFSCHRIJF_CATEGORIEEN = ['TELEFOON', 'ELEKTRONICA', 'GEREEDSCHAP', 'KLEDING', 'OVERIG'] as const
export type InnameAfschrijfCategorie = (typeof INNAME_AFSCHRIJF_CATEGORIEEN)[number]

export const INNAME_AFSCHRIJF_CATEGORIE_LABELS: Record<InnameAfschrijfCategorie, string> = {
  TELEFOON: 'telefoon',
  ELEKTRONICA: 'elektronica',
  GEREEDSCHAP: 'gereedschap',
  KLEDING: 'kleding en PBM',
  OVERIG: 'overig',
}

export const INNAME_WAARDE_BRONNEN = ['AANSCHAFWAARDE', 'CATALOGUSPRIJS', 'LAATSTE_INKOOP'] as const
export type InnameWaardeBron = (typeof INNAME_WAARDE_BRONNEN)[number]

export const INNAME_WAARDE_BRON_LABELS: Record<InnameWaardeBron, string> = {
  AANSCHAFWAARDE: 'aanschafwaarde',
  CATALOGUSPRIJS: 'catalogusprijs',
  LAATSTE_INKOOP: 'laatste inkoopprijs',
}

/**
 * De voorraad-categorie van een artikel naar de rij van de afschrijvingstabel.
 * Onbekende of nieuwe categorieën vallen onder OVERIG (nooit een fout).
 */
export function innameAfschrijfCategorie(voorraadCategorie: string | null | undefined): InnameAfschrijfCategorie {
  switch (voorraadCategorie) {
    case 'TELEFOON':
    case 'SIMKAART':
      return 'TELEFOON'
    case 'LAPTOP':
    case 'TABLET':
    case 'ELEKTRONICA':
      return 'ELEKTRONICA'
    case 'GEREEDSCHAP':
    case 'KOFFERS':
      return 'GEREEDSCHAP'
    case 'WERKKLEDING':
    case 'PBM':
    case 'KLEDING':
      return 'KLEDING'
    default:
      return 'OVERIG'
  }
}

/** Afschrijvingstermijn (maanden) van een categorie volgens de tabel. */
export function afschrijfMaandenVoor(tabel: InnameAfschrijvingstabel, categorie: InnameAfschrijfCategorie): number {
  switch (categorie) {
    case 'TELEFOON':
      return tabel.telefoonMaanden
    case 'ELEKTRONICA':
      return tabel.elektronicaMaanden
    case 'GEREEDSCHAP':
      return tabel.gereedschapMaanden
    case 'KLEDING':
      return tabel.kledingMaanden
    default:
      return tabel.overigMaanden
  }
}

export interface InnameWaardeItem {
  categorie: InnameAfschrijfCategorie
  /** Aantal stuks (bulk); default 1. */
  aantal?: number
  /** Aanschafwaarde PER STUK van dit exemplaar (Decimal-string of getal in euro's). */
  aanschafwaarde?: string | number | null
  /** ISO-datum of -datetime van de aanschaf. */
  aanschafDatum?: string | Date | null
  /** Catalogusprijs per stuk. */
  catalogusPrijs?: string | number | null
  /** Prijs per stuk van de laatste inkoopregel. */
  laatsteInkoopPrijs?: string | number | null
  /** Uitgifte aan de medewerker: start van de afschrijving als de aanschafdatum ontbreekt. */
  uitgegevenOp?: string | Date | null
  /**
   * Bulk: de uitgiftes aan de medewerker, elk met zijn eigen datum. Zonder
   * aanschafdatum rekent elke partij met zijn eigen leeftijd (zie
   * `verdeelOverPartijen`); leeg = alle stuks vanaf `uitgegevenOp`.
   */
  partijen?: readonly InnameUitgiftePartij[] | null
}

/** Eén uitgifte van een bulkartikel aan de medewerker: zoveel stuks op dit moment. */
export interface InnameUitgiftePartij {
  moment: string | Date
  aantal: number
}

export type InnameWaardeLeegReden = 'GEEN_PRIJS' | 'GEEN_DATUM'

export interface InnameWaardeVoorstel {
  /** Het voorgestelde bedrag (Decimal-string, 2 decimalen) voor alle stuks samen; null = geen voorstel. */
  bedrag: string | null
  /** Het voorgestelde bedrag per stuk; null = geen voorstel. */
  perStuk: string | null
  aantal: number
  bron: InnameWaardeBron | null
  /** De prijs per stuk waar de afschrijving op rekent. */
  basisPerStuk: string | null
  /** Hele maanden tussen de startdatum en de peildatum; null bij stuks van verschillende leeftijd (partijen). */
  maandenOud: number | null
  afschrijfMaanden: number
  /** Waar de afschrijving vanaf rekent. */
  startBron: 'AANSCHAFDATUM' | 'UITGIFTEDATUM' | null
  /** Het minimum restwaardepercentage bepaalde het bedrag (de lineaire waarde lag eronder). */
  minimumToegepast: boolean
  /** Waarom er geen voorstel is. */
  leegReden: InnameWaardeLeegReden | null
  /** Leesbare berekening voor de backoffice, met de prijs waar hij op rust. */
  toelichting: string
  /**
   * Dezelfde berekening ZONDER de aanschaf-, catalogus- of inkoopprijs: voor het
   * document en de portal van de medewerker. Een inkoopprijs is interne
   * kostprijs en gaat niet naar buiten; de leeftijd en de afschrijving wel.
   */
  toelichtingMedewerker: string
}

/**
 * Een prijs per stuk in euro's naar hele tienduizendsten (vier decimalen, zoals
 * de inkoopprijs in de database); null bij leeg, ongeldig of niet-positief.
 * Niet naar centen: een inkoopprijs van 0,0425 per stuk mag pas na het
 * vermenigvuldigen met het aantal worden afgerond (review K5: 500 x 0,0425 =
 * 21,25, niet 500 x 0,04 = 20,00).
 */
function naarTienduizendsten(bedrag: string | number | null | undefined): number | null {
  if (bedrag === null || bedrag === undefined) return null
  const tekst = typeof bedrag === 'number' ? (Number.isFinite(bedrag) ? bedrag.toFixed(6) : '') : String(bedrag).trim()
  const m = /^(\d{1,12})(?:\.(\d{1,6}))?$/.exec(tekst)
  if (!m) return null
  const heel = Number(m[1])
  const decimalen = (m[2] ?? '').padEnd(5, '0')
  // Vier decimalen plus één om half-omhoog af te ronden (prijzen met 5+ decimalen).
  const e4 = heel * 10000 + Number(decimalen.slice(0, 4)) + (Number(decimalen[4]) >= 5 ? 1 : 0)
  return e4 > 0 ? e4 : null
}

/** Tienduizendsten als Decimal-string: twee decimalen, of vier als de prijs die echt heeft. */
function vanTienduizendsten(e4: number): string {
  const heel = Math.floor(e4 / 10000)
  const rest = e4 % 10000
  return rest % 100 === 0 ? `${heel}.${String(rest / 100).padStart(2, '0')}` : `${heel}.${String(rest).padStart(4, '0')}`
}

/** Een prijs per stuk (tienduizendsten) als NL-bedrag, met vier decimalen alleen als dat nodig is. */
function nlPrijs(e4: number): string {
  const [heel, decimalen] = vanTienduizendsten(e4).split('.')
  return `€ ${heel!.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimalen}`
}

function vanCenten(centen: number): string {
  const heel = Math.floor(centen / 100)
  const rest = centen % 100
  return `${heel}.${String(rest).padStart(2, '0')}`
}

/**
 * Het product van niet-negatieve gehele factoren (maal een optionele BigInt-
 * factor) gedeeld door de noemer, half-omhoog afgerond. Exact (BigInt, geen
 * floats), ook bij grote aantallen.
 */
function productDeelAfgerond(factoren: readonly number[], noemer: number, extra: bigint = BigInt(1)): number {
  const teller = factoren.reduce((product, f) => product * BigInt(f), extra)
  const n = BigInt(noemer)
  return Number((BigInt(2) * teller + n) / (BigInt(2) * n))
}

/** Percentage 0-100 (hooguit 2 decimalen) naar basispunten (0-10000). */
function naarBasispunten(pct: string | number): number {
  const tekst = typeof pct === 'number' ? pct.toFixed(2) : String(pct).trim()
  const m = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(tekst)
  if (!m) return 0
  const bp = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'))
  return Math.min(Math.max(bp, 0), 10000)
}

/** De NL-kalenderdag (jaar, maand 1-12, dag) van een moment; null bij een ongeldige datum. */
function nlDag(waarde: string | Date | null | undefined): { jaar: number; maand: number; dag: number } | null {
  if (waarde === null || waarde === undefined || waarde === '') return null
  // Een kale datum (yyyy-mm-dd) is al een kalenderdag: niet via een tijdzone halen.
  if (typeof waarde === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(waarde)
    if (m) return { jaar: Number(m[1]), maand: Number(m[2]), dag: Number(m[3]) }
  }
  const d = waarde instanceof Date ? waarde : new Date(waarde)
  if (Number.isNaN(d.getTime())) return null
  const delen = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const deel = (type: string) => Number(delen.find((p) => p.type === type)?.value ?? '0')
  return { jaar: deel('year'), maand: deel('month'), dag: deel('day') }
}

/**
 * Hele maanden van `van` tot `tot`, gerekend op de NL-kalenderdag van beide
 * momenten (een nog niet volle maand telt niet; nooit negatief). De telling
 * zelf is `heleMaandenTussen` uit de ketenregeling: één definitie van "hele
 * maanden" in het systeem.
 */
export function innameMaandenOud(van: string | Date, tot: string | Date): number | null {
  const a = nlDag(van)
  const b = nlDag(tot)
  if (!a || !b) return null
  return heleMaandenTussen(new Date(Date.UTC(a.jaar, a.maand - 1, a.dag)), new Date(Date.UTC(b.jaar, b.maand - 1, b.dag)))
}

function nlBedrag(centen: number): string {
  const heel = Math.floor(centen / 100)
  const rest = String(centen % 100).padStart(2, '0')
  return `€ ${String(heel).replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${rest}`
}

/**
 * Verdeel `aantal` open stuks van een bulkregel over de uitgiftepartijen.
 * Welke stuks er nog openstaan is niet te zien; we volgen FIFO (wat het eerst
 * is uitgegeven, komt het eerst terug), dus de open stuks zijn de nieuwste
 * partijen. Stuks die de partijen niet dekken (bezit van vóór het ledger)
 * rekenen met de oudste bekende uitgifte, en zonder partijen met `terugval`.
 */
export function verdeelOverPartijen(
  aantal: number,
  partijen: readonly InnameUitgiftePartij[],
  terugval: string | Date | null,
): Array<{ moment: string | Date | null; aantal: number }> {
  const tijd = (m: string | Date) => (m instanceof Date ? m.getTime() : new Date(m).getTime())
  const geldig = partijen
    .filter((p) => Math.trunc(p.aantal) > 0 && nlDag(p.moment) !== null)
    .sort((a, b) => tijd(b.moment) - tijd(a.moment))
  const uit: Array<{ moment: string | Date | null; aantal: number }> = []
  let rest = Math.max(0, Math.trunc(aantal))
  for (const p of geldig) {
    if (rest <= 0) break
    const deel = Math.min(rest, Math.trunc(p.aantal))
    uit.push({ moment: p.moment, aantal: deel })
    rest -= deel
  }
  if (rest > 0) uit.push({ moment: geldig.length > 0 ? geldig[geldig.length - 1]!.moment : terugval, aantal: rest })
  return uit
}

/**
 * Stel de waarde voor van een middel dat ontbreekt of beschadigd is.
 *
 * @param item   prijzen en datums van het middel (per stuk)
 * @param tabel  de instelbare afschrijvingstabel
 * @param datum  de peildatum (meestal de einddatum of vandaag)
 */
export function stelWaardeVoor(
  item: InnameWaardeItem,
  tabel: InnameAfschrijvingstabel,
  datum: string | Date,
): InnameWaardeVoorstel {
  const aantal = Math.max(1, Math.trunc(item.aantal ?? 1))
  const afschrijfMaanden = Math.max(1, Math.trunc(afschrijfMaandenVoor(tabel, item.categorie)))
  const leeg = (leegReden: InnameWaardeLeegReden, toelichting: string): InnameWaardeVoorstel => ({
    bedrag: null,
    perStuk: null,
    aantal,
    bron: null,
    basisPerStuk: null,
    maandenOud: null,
    afschrijfMaanden,
    startBron: null,
    minimumToegepast: false,
    leegReden,
    toelichting,
    toelichtingMedewerker: 'Het bedrag is door de werkgever vastgesteld.',
  })

  // 1. De basis: aanschafwaarde van het exemplaar, anders catalogus, anders laatste inkoop.
  const kandidaten: Array<[InnameWaardeBron, number | null]> = [
    ['AANSCHAFWAARDE', naarTienduizendsten(item.aanschafwaarde)],
    ['CATALOGUSPRIJS', naarTienduizendsten(item.catalogusPrijs)],
    ['LAATSTE_INKOOP', naarTienduizendsten(item.laatsteInkoopPrijs)],
  ]
  const gevonden = kandidaten.find(([, centen]) => centen !== null)
  if (!gevonden || gevonden[1] === null) {
    return leeg('GEEN_PRIJS', 'Geen aanschafwaarde, catalogusprijs of inkoopprijs bekend: vul het bedrag zelf in, met reden.')
  }
  const [bron, basis] = gevonden

  // 2. De leeftijd: vanaf de aanschaf, anders vanaf de uitgifte aan de medewerker.
  //    Een bulkregel rekent dan per uitgiftepartij met zijn eigen datum (review B,
  //    klein: niet alle stuks met de laatste uitgifte).
  const partijen = item.partijen ?? []
  const startBron: InnameWaardeVoorstel['startBron'] = nlDag(item.aanschafDatum)
    ? 'AANSCHAFDATUM'
    : nlDag(item.uitgegevenOp) || partijen.some((p) => nlDag(p.moment) !== null)
      ? 'UITGIFTEDATUM'
      : null
  const stukken =
    startBron === 'AANSCHAFDATUM'
      ? [{ moment: item.aanschafDatum ?? null, aantal }]
      : verdeelOverPartijen(aantal, partijen, item.uitgegevenOp ?? null)
  const leeftijden = stukken.map((st) => ({ aantal: st.aantal, maandenOud: st.moment ? innameMaandenOud(st.moment, datum) : null }))
  if (startBron === null || leeftijden.some((l) => l.maandenOud === null)) {
    return {
      ...leeg(
        'GEEN_DATUM',
        `${INNAME_WAARDE_BRON_LABELS[bron].replace(/^./, (c) => c.toUpperCase())} ${nlPrijs(basis)} bekend, maar geen aanschaf- of uitgiftedatum: de afschrijving is niet te berekenen. Vul het bedrag zelf in, met reden.`,
      ),
      bron,
      basisPerStuk: vanTienduizendsten(basis),
    }
  }
  // Stuks van dezelfde leeftijd samen, de oudste eerst.
  const perLeeftijd = new Map<number, number>()
  for (const l of leeftijden) perLeeftijd.set(l.maandenOud!, (perLeeftijd.get(l.maandenOud!) ?? 0) + l.aantal)
  const groepen = [...perLeeftijd].sort((a, b) => b[0] - a[0])

  // 3. Lineair afschrijven, met het minimum restwaardepercentage als ondergrens.
  //    De fracties exact vergeleken (resterend/afschrijf tegen bp/10000) op één
  //    noemer, en pas aan het eind afgerond: eerst het totaal over alle stuks,
  //    dan naar centen (3 x 19,99 x 8/12 = 39,98, niet 3 x 13,33 = 39,99).
  const minimumBp = naarBasispunten(tabel.minimumRestwaardePct)
  const noemer = afschrijfMaanden * 10000
  let teller = BigInt(0)
  let minimumToegepast = false
  for (const [maandenOud, stuks] of groepen) {
    const resterend = Math.max(afschrijfMaanden - maandenOud, 0)
    const minimum = minimumBp * afschrijfMaanden > resterend * 10000
    if (minimum) minimumToegepast = true
    teller += BigInt(stuks) * BigInt(minimum ? minimumBp * afschrijfMaanden : resterend * 10000)
  }
  // De basis staat in tienduizendsten: door 100 extra naar centen, pas hier afgerond.
  const totaal = productDeelAfgerond([basis], noemer * 100, teller)
  // Per stuk: bij één leeftijd de waarde van een stuk, anders het gemiddelde.
  const perStuk = productDeelAfgerond([basis], noemer * 100 * aantal, teller)
  const maandenOud = groepen.length === 1 ? groepen[0]![0] : null

  const maanden = (n: number) => `${n} ${n === 1 ? 'maand' : 'maanden'}`
  const leeftijdTekst =
    maandenOud !== null
      ? `${maanden(maandenOud)} oud (vanaf de ${startBron === 'AANSCHAFDATUM' ? 'aanschaf' : 'uitgifte'})`
      : `${groepen.map(([m, n]) => `${n} ${n === 1 ? 'stuk' : 'stuks'} ${maanden(m)} oud`).join(', ')} (vanaf de uitgifte van elke partij)`
  const delen = [
    `${INNAME_WAARDE_BRON_LABELS[bron].replace(/^./, (c) => c.toUpperCase())} ${nlPrijs(basis)}${aantal > 1 ? ' per stuk' : ''}`,
    leeftijdTekst,
    `lineair afgeschreven over ${afschrijfMaanden} maanden (${INNAME_AFSCHRIJF_CATEGORIE_LABELS[item.categorie]})`,
  ]
  if (minimumToegepast) {
    delen.push(`minimum restwaarde ${String(tabel.minimumRestwaardePct).replace('.', ',').replace(/,00$/, '')}%`)
  }
  const uitkomst =
    aantal === 1
      ? nlBedrag(perStuk)
      : `${aantal} stuks: ${nlBedrag(totaal)} (afgerond ${nlBedrag(perStuk)} per stuk${maandenOud === null ? ' gemiddeld' : ''})`
  // Voor de medewerker: wel de leeftijd en de afschrijving, niet de prijs erachter.
  const delenMedewerker = ['Dagwaarde na afschrijving', ...delen.slice(1)]

  return {
    bedrag: vanCenten(totaal),
    perStuk: vanCenten(perStuk),
    aantal,
    bron,
    basisPerStuk: vanTienduizendsten(basis),
    maandenOud,
    afschrijfMaanden,
    startBron,
    minimumToegepast,
    leegReden: null,
    toelichting: `${delen.join(', ')}: ${uitkomst}.`,
    toelichtingMedewerker: `${delenMedewerker[0]}: ${delenMedewerker.slice(1).join(', ')}.`,
  }
}
